import { Button } from "./ui/button";
import { ArrowRight, Zap, Shield, Code, Package, Rocket, CheckCircle2 } from "lucide-react";
import { useEffect } from "react";

export function LandingPage() {
  useEffect(() => {
    // Add structured data for SEO
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      "name": "mysume",
      "description": "Build beautiful resumes from YAML. Experience the power of structured data to create professional resumes with ease. Type-safe, version-controlled, and developer-friendly.",
      "url": "https://mysume.com",
      "applicationCategory": "BusinessApplication",
      "operatingSystem": "Web",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD"
      },
      "featureList": [
        "Type-Safe YAML",
        "Instant Preview",
        "Version Control",
        "Multiple Templates",
        "PDF Export",
        "Developer Friendly"
      ]
    };

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify(structuredData);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen bg-zinc-900 text-white">
      {/* Navigation */}
      <header>
        <nav className="border-b border-zinc-800 bg-zinc-900" aria-label="Main navigation">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <div className="flex items-center gap-2.5">
              <a href="/" className="cursor-pointer">
                <h1 className="text-xl sm:text-2xl font-semibold tracking-tight">
                  <span className="text-white">my</span>
                  <span className="text-emerald-500">sume</span>
                </h1>
              </a>
            </div>
            <div className="flex items-center gap-4">
              <a href="/resume-builder">
                <Button className="bg-emerald-600 hover:bg-emerald-500 text-white border-emerald-600 hover:border-emerald-500 cursor-pointer">
                  Get Started
                </Button>
              </a>
            </div>
          </div>
        </div>
      </nav>
      </header>

      <main>
      {/* Hero Section */}
      <section className="relative overflow-hidden border-b border-zinc-800" aria-label="Hero section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32">
          <div className="mx-auto max-w-4xl text-center">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight mb-6">
              Build Beautiful Resumes
              <br />
              <span className="text-emerald-500">From YAML</span>
            </h1>
            <p className="text-xl text-zinc-400 mb-8 max-w-2xl mx-auto">
              Experience the power of structured data to create professional resumes with ease.
              Type-safe, version-controlled, and developer-friendly.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <a href="/resume-builder">
                <Button size="lg" className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-500 text-white border-emerald-600 hover:border-emerald-500 cursor-pointer">
                  Get Started
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </a>
            </div>
            {/* Hero Image */}
            <div className="mt-12 rounded-lg border border-zinc-800 overflow-hidden shadow-2xl bg-zinc-900">
              <img 
                src="/img/hero-image.png" 
                alt="mysume Resume Builder Interface" 
                className="w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 sm:py-32 border-b border-zinc-800" aria-label="Features">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center mb-16">
            <h2 className="text-3xl font-bold tracking-tight mb-4 text-white">
              Everything you need to build professional resumes
            </h2>
            <p className="text-lg text-zinc-400">
              Powerful features that make resume creation fast, easy, and maintainable.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <FeatureCard
              icon={<Shield className="h-6 w-6" />}
              title="Type-Safe YAML"
              description="Write your resume in YAML with full schema validation. Catch errors before they become problems."
            />
            <FeatureCard
              icon={<Zap className="h-6 w-6" />}
              title="Instant Preview"
              description="See your changes in real-time as you edit. No build steps, no waiting—just instant feedback."
            />
            <FeatureCard
              icon={<Code className="h-6 w-6" />}
              title="Version Control"
              description="Track changes, save versions, and compare different iterations of your resume with ease."
            />
            <FeatureCard
              icon={<Package className="h-6 w-6" />}
              title="Multiple Templates"
              description="Choose from beautiful, professional templates. More templates coming soon."
            />
            <FeatureCard
              icon={<Rocket className="h-6 w-6" />}
              title="PDF Export"
              description="Generate high-quality PDFs ready for job applications. Print-perfect every time."
            />
            <FeatureCard
              icon={<CheckCircle2 className="h-6 w-6" />}
              title="Developer Friendly"
              description="Built for developers who want to manage their resume like code. Git-friendly format."
            />
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-24 sm:py-32 border-b border-zinc-800 bg-zinc-800/30" aria-label="How it works">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-4xl">
            <h2 className="text-3xl font-bold tracking-tight text-center mb-16 text-white">
              Simple to use with unmatched developer experience
            </h2>
            <p className="text-lg text-zinc-400 text-center mb-12">
              It's quick and easy to get started with mysume to build your professional resume.
            </p>

            <div className="space-y-12">
              <Step
                number="1"
                title="Write your resume in YAML"
                description="Start with a simple YAML file. Define your personal information, work experience, education, and skills in a clean, structured format."
              />
              <Step
                number="2"
                title="Choose a template"
                description="Select from our collection of professional templates. Each template is designed to make your resume stand out."
              />
              <Step
                number="3"
                title="Preview and export"
                description="See your resume come to life in real-time. When you're happy, export it as a beautiful PDF ready for applications."
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 sm:py-32" aria-label="Call to action">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight mb-4 text-white">
              Ready to build your resume?
            </h2>
            <p className="text-lg text-zinc-400 mb-8">
              Get started in seconds. No account required, no credit card needed.
            </p>
            <a href="/resume-builder">
              <Button size="lg" className="bg-emerald-600 hover:bg-emerald-500 text-white border-emerald-600 hover:border-emerald-500 cursor-pointer">
                Get Started
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </a>
          </div>
        </div>
      </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-zinc-800 py-12" role="contentinfo">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="text-sm text-zinc-400">
              © 2025 mysume. Built with ❤️ for developers.
            </div>
            <div className="flex gap-6 text-sm">
              <a href="#" className="text-zinc-400 hover:text-white transition-colors">
                Documentation
              </a>
              <a href="#" className="text-zinc-400 hover:text-white transition-colors">
                GitHub
              </a>
              <a href="#" className="text-zinc-400 hover:text-white transition-colors">
                Support
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

function FeatureCard({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <div className="p-6 rounded-lg border border-zinc-800 bg-zinc-800/50">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 rounded-md bg-emerald-500/10 text-emerald-500">{icon}</div>
        <h3 className="text-lg font-semibold text-white">{title}</h3>
      </div>
      <p className="text-zinc-400">{description}</p>
    </div>
  );
}

function Step({
  number,
  title,
  description,
}: {
  number: string;
  title: string;
  description: string;
}) {
  return (
    <div className="flex gap-6">
      <div className="flex-shrink-0">
        <div className="flex items-center justify-center w-12 h-12 rounded-full bg-emerald-600 text-white font-bold text-lg">
          {number}
        </div>
      </div>
      <div>
        <h3 className="text-xl font-semibold mb-2 text-white">{title}</h3>
        <p className="text-zinc-400">{description}</p>
      </div>
    </div>
  );
}

